import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Loader2, MessageSquare, ArrowDown } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { RankAvatarBadge } from '../../RankAvatarBadge';
import VideoPlayer, { isValidVideoUrl } from '../../VideoPlayer';
import { MessageRow } from '../chat/MessageRow';
import { Composer } from '../chat/Composer';
import { ImageLightbox } from '../chat/ImageLightbox';
import { buildTimeline, mergeMessages, latestStamp } from '../chat/chatUtils';
import { getDmMessages, sendDm, editDm, deleteDm, reactToDm, sendDmTyping, getDmTyping, uploadDmImage } from '../../../services/api';
import { toast } from '../../../hooks/use-toast';

const DM_MAX = 2000;

// One direct-message conversation, rendered with the chat's message rows, composer and polling
export const DmThread = ({ conversation, me, canModerate, onBack, onMessageSent, showBack }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState([]);
  const [replyToId, setReplyToId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [atBottom, setAtBottom] = useState(true);
  const [newBelow, setNewBelow] = useState(0);
  const containerRef = useRef(null);
  const lastSyncRef = useRef(null);
  const other = conversation.otherUser || {};

  const bumpSync = (list) => { const s = latestStamp(list); if (s && (!lastSyncRef.current || s > lastSyncRef.current)) lastSyncRef.current = s; };
  const scrollToBottom = useCallback((smooth = false) => {
    const c = containerRef.current; if (c) c.scrollTo({ top: c.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setMessages([]); setReplyToId(null); setEditingId(null); setNewBelow(0); setAtBottom(true); lastSyncRef.current = null;
    getDmMessages(conversation.id).then((data) => {
      if (cancelled) return;
      setMessages(data); bumpSync(data); lastSyncRef.current = lastSyncRef.current || new Date().toISOString();
      setLoading(false);
      requestAnimationFrame(() => scrollToBottom());
    }).catch(() => { if (!cancelled) { setLoading(false); toast({ title: 'Messages', description: 'Failed to load messages', variant: 'destructive' }); } });
    return () => { cancelled = true; };
  }, [conversation.id, scrollToBottom]);

  // Incremental sync + typing, every 3s while the tab is visible
  useEffect(() => {
    const tick = async () => {
      if (!lastSyncRef.current || document.visibilityState !== 'visible') return;
      getDmTyping(conversation.id).then((d) => setTyping(d.typing || [])).catch(() => {});
      try {
        const delta = await getDmMessages(conversation.id, lastSyncRef.current);
        if (!delta.length) return;
        setMessages((prev) => {
          const known = new Set(prev.map((m) => m.id));
          const fresh = delta.filter((m) => !m.deleted && !known.has(m.id) && m.senderId !== me.id).length;
          if (fresh && !atBottom) setNewBelow((n) => n + fresh);
          return mergeMessages(prev, delta);
        });
        bumpSync(delta);
        if (atBottom) requestAnimationFrame(() => scrollToBottom(true));
      } catch { /* silent */ }
    };
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, [conversation.id, atBottom, me.id, scrollToBottom]);

  const handleScroll = () => {
    const c = containerRef.current; if (!c) return;
    const near = c.scrollHeight - c.scrollTop - c.clientHeight < 80;
    setAtBottom(near); if (near) setNewBelow(0);
  };

  const fail = (e, fallback) => toast({ title: 'Messages', description: e?.response?.data?.detail || fallback, variant: 'destructive' });

  const send = async (text, _ids, replyTo = null, image = null) => {
    try {
      const sent = await sendDm(conversation.id, text, replyTo?.id || null, image);
      setMessages((prev) => mergeMessages(prev, [sent])); bumpSync([sent]);
      setReplyToId(null); setAtBottom(true); onMessageSent?.(sent);
      requestAnimationFrame(() => scrollToBottom(true));
    } catch (e) { fail(e, 'Failed to send message'); throw e; }
  };
  const saveEdit = async (id, text) => {
    try { const res = await editDm(conversation.id, id, text); setMessages((prev) => mergeMessages(prev, [res])); bumpSync([res]); setEditingId(null); }
    catch (e) { fail(e, 'Failed to edit message'); throw e; }
  };
  const remove = async (id) => {
    try { await deleteDm(conversation.id, id); setMessages((prev) => prev.filter((m) => m.id !== id)); }
    catch (e) { fail(e, 'Failed to delete message'); }
  };
  const react = async (id, emoji) => {
    try { const res = await reactToDm(conversation.id, id, emoji); setMessages((prev) => mergeMessages(prev, [res])); bumpSync([res]); }
    catch (e) { fail(e, 'Failed to react'); }
  };
  const heartbeat = () => sendDmTyping(conversation.id).catch(() => {});
  const uploadImage = async (file) => {
    try { return await uploadDmImage(conversation.id, file); }
    catch (e) { fail(e, 'Image upload failed'); throw e; }
  };

  // Owner/moderator video links render as players, like before
  const renderMedia = (msg) => {
    const role = msg.senderId === me.id ? me.role : other.role;
    if ((role === 'owner' || role === 'moderator') && isValidVideoUrl(msg.content)) {
      return <div className="w-full max-w-[420px] rounded-lg overflow-hidden border border-zinc-700/80 bg-black" data-testid="dm-video-message"><VideoPlayer url={msg.content} showThumbnail className="w-full" /></div>;
    }
    return null;
  };

  const meSender = { id: me.id, displayName: me.displayName, avatarUrl: me.avatarUrl, role: me.role };
  const otherSender = { id: other.id, displayName: other.displayName, avatarUrl: other.avatarUrl, role: other.role, rank: other.rank };
  const decorated = messages.map((m) => ({ ...m, sender: m.senderId === me.id ? meSender : otherSender }));
  const timeline = buildTimeline(decorated, null, me.id, null);
  const typingLabel = typing.length ? `${other.displayName || 'Someone'} is typing…` : '';

  return (
    <div className="flex flex-col h-full min-h-0 bg-[#121215]" data-testid="dm-thread">
      <div className="h-14 px-3 sm:px-4 border-b border-zinc-800/80 flex items-center gap-3 shrink-0">
        {showBack && (
          <button onClick={onBack} className="p-1.5 -ml-1 rounded-md text-amber-500 hover:bg-zinc-800" title="Back" data-testid="dm-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        )}
        <div className="relative flex-shrink-0">
          <Avatar className="w-9 h-9 border border-zinc-700/80">
            <AvatarImage src={other.avatarUrl} />
            <AvatarFallback className="bg-zinc-800 text-amber-500 text-sm">{other.displayName?.charAt(0) || '?'}</AvatarFallback>
          </Avatar>
          <RankAvatarBadge rank={other.rank} />
        </div>
        <div className="min-w-0">
          <p className="font-headline text-zinc-100 tracking-wide truncate" data-testid="dm-thread-name">{other.displayName || 'Unknown'}</p>
          <p className="text-[10px] font-typewriter text-zinc-500 truncate">{other.role === 'owner' ? 'Lead Detective' : other.role === 'moderator' ? 'Moderator' : 'Private line'}</p>
        </div>
      </div>

      <div className="flex-1 relative min-h-0 flex flex-col">
        <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto overflow-x-hidden py-2" data-testid="dm-messages-container">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>
          ) : timeline.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 font-typewriter">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No messages yet.</p>
              <p className="text-xs text-zinc-600 mt-1">Open the line below.</p>
            </div>
          ) : timeline.map((item) => {
            if (item.kind === 'day') return (
              <div key={item.id} className="flex items-center my-3 px-4 font-typewriter text-[11px] tracking-widest text-amber-500/80 uppercase before:flex-1 before:border-t before:border-zinc-800 before:mr-3 after:flex-1 after:border-t after:border-zinc-800 after:ml-3" data-testid="day-divider">{item.label}</div>
            );
            if (item.kind !== 'msg') return null;
            const m = item.msg;
            return (
              <MessageRow key={m.id} msg={m} grouped={item.grouped} me={me} members={[]} allowMentions={false} maxLength={DM_MAX}
                canDelete={canModerate || m.senderId === me.id} onTyping={heartbeat} onOpenImage={setLightbox} onUploadImage={uploadImage}
                renderMedia={renderMedia} ownerLabel="Daria"
                isEditing={editingId === m.id} isReplying={replyToId === m.id}
                onReply={(msg) => { setEditingId(null); setReplyToId(msg.id); }}
                onEdit={(id) => { setReplyToId(null); setEditingId(id); }}
                onDelete={remove} onSaveEdit={saveEdit} onCancelEdit={() => setEditingId(null)} onReact={react}
                onSendReply={(msg, text, _ids, image) => send(text, [], msg, image)} onCancelReply={() => setReplyToId(null)} />
            );
          })}
        </div>

        {typingLabel && (
          <div className="px-4 py-1 text-[11px] font-typewriter text-amber-500/80 flex items-center gap-1.5 shrink-0" data-testid="typing-indicator">
            <span className="flex gap-0.5"><span className="w-1 h-1 rounded-full bg-amber-500 animate-bounce" /><span className="w-1 h-1 rounded-full bg-amber-500 animate-bounce [animation-delay:150ms]" /><span className="w-1 h-1 rounded-full bg-amber-500 animate-bounce [animation-delay:300ms]" /></span>
            {typingLabel}
          </div>
        )}

        {(newBelow > 0 || !atBottom) && !loading && (
          <button onClick={() => { setAtBottom(true); setNewBelow(0); scrollToBottom(true); }} data-testid="jump-to-latest"
            className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-amber-600 text-black text-[11px] font-headline tracking-wider px-3 py-1.5 rounded-full shadow-lg hover:bg-amber-500 transition-colors z-10">
            <ArrowDown className="w-3.5 h-3.5" />{newBelow > 0 ? `${newBelow} new` : 'Latest'}
          </button>
        )}
      </div>

      {!replyToId && !editingId && (
        <div className="border-t border-zinc-800/80 p-3 shrink-0 bg-[#121215]" data-testid="dm-composer">
          <Composer members={[]} allowMentions={false} maxLength={DM_MAX} onTyping={heartbeat} onUploadImage={uploadImage}
            placeholder={`Message ${other.displayName || ''}…`} testId="message-input" onSubmit={(text, _ids, image) => send(text, [], null, image)} />
        </div>
      )}
      <ImageLightbox url={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
};

export default DmThread;
